import { t } from "@lingui/macro"
import { Stack, Typography, Button, FormHelperText } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import { observer } from "mobx-react"
import { useCallback, useMemo, useEffect } from "react"

import { FormPanel } from "src/components/FormPanel"
import { SegmentPickerModal } from "src/modals/segment-picker"
import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"
import { SegmentFormStore } from "src/modals/segment-form/store"
import { SegmentHierarchyListItem } from "src/modals/segment-form/components/SegmentHierarchyListItem/index"
import { SegmentPickerStore } from "src/components/SegmentPicker/store"
import { ISegmentType } from "src/components/SegmentPicker/type"
import { SEGMENT_TYPE_GROUP_STANDARD } from "src/components/SegmentPicker/constants"

export const SegmentsHierarchySection = observer(() => {
    const gstore = useStore(GlobalStore)
    const store = useStore(SegmentFormStore)
    const segmentPickerStore = useStore(SegmentPickerStore)

    const handleChange = useCallback(async (segmentIds) => {
        await store.updateParentSegmentAndFormFields(segmentIds)
        gstore.modals.pop()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const initialSegmentIds = useMemo(() => {
        return store.parentSegment != null ? [store.parentSegment.id] : []
    }, [store.parentSegment])

    const onCloseSegmentPicker = useCallback(() => {
        segmentPickerStore.setInitialSegmentIds([])
        gstore.modals.pop()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectedType = store.formFields.get("segment_type") as
        | ISegmentType
        | undefined

    // Update the allowedSegmentTypes in the segmentPickerStore based on the selected segment type
    useEffect(() => {
        if (selectedType != null) {
            // Find the index of the selected type in the standard hierarchy
            const standardTypes = SEGMENT_TYPE_GROUP_STANDARD
            const selectedIndex = standardTypes.indexOf(selectedType)

            if (selectedIndex > 0) {
                // Get all types that come before the selected type in the hierarchy
                const parentTypes = standardTypes.slice(0, selectedIndex)
                segmentPickerStore.allowedSegmentTypes = parentTypes
            } else {
                // If it's a top-level type (propertyowner, legalentity), allow no parent types
                segmentPickerStore.allowedSegmentTypes = []
            }
        } else {
            // If no type is selected, use all types
            segmentPickerStore.allowedSegmentTypes = SEGMENT_TYPE_GROUP_STANDARD
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.formFields.get("segment_type"), segmentPickerStore])

    const selectedSegmentType = store.formFields.get("segment_type") as
        | ISegmentType
        | undefined

    const handleSegmentPickerOpen = useCallback(() => {
        // Pre-initialize the segmentPickerStore with the parent segment
        if (store.parentSegment != null) {
            segmentPickerStore.setInitialSegmentIds([store.parentSegment.id])
        }

        gstore.modals.open(
            () => (
                <SegmentPickerModal
                    initialSegmentIds={initialSegmentIds}
                    accessGroupId={gstore.session.accessGroupId}
                    onChange={handleChange}
                    onClose={onCloseSegmentPicker}
                    segmentListItemPrefixType="radio"
                    allowedSegmentTypes={segmentPickerStore.allowedSegmentTypes}
                />
            ),
            { variant: "slide-up-w995" },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialSegmentIds,
        store.parentSegment,
        gstore.session.accessGroupId,
        segmentPickerStore.allowedSegmentTypes,
    ])

    // Ensure segmentPickerStore is initialized with parent segment when it changes
    useEffect(() => {
        if (store.parentSegment != null) {
            segmentPickerStore.setInitialSegmentIds([store.parentSegment.id])
        }
    }, [store.parentSegment, segmentPickerStore])

    const segmentTypeError = useMemo(() => {
        return store.formFields.error("parent_segment_id")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.formFields.error("parent_segment_id")])

    return (
        <FormPanel
            sections={[
                {
                    header: t`segment-form-modal.hierarchy`,
                    content: (
                        <Stack spacing={2}>
                            <Typography variant="body1" color="text.primary">
                                {t`segment-form-modal.section.description`}
                            </Typography>
                            {store.parentSegment != null && (
                                <SegmentHierarchyListItem
                                    {...store.parentSegment}
                                />
                            )}
                            <Button
                                variant="outlined"
                                color="info"
                                size="medium"
                                startIcon={
                                    store.parentSegment != null ? (
                                        <EditIcon />
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                sx={{
                                    backgroundColor: "transparent",
                                    alignSelf: "flex-start",
                                }}
                                onClick={handleSegmentPickerOpen}
                                disabled={selectedSegmentType == null}
                            >
                                {store.parentSegment != null
                                    ? t`global.edit`
                                    : t`global.add`}
                            </Button>
                            {segmentTypeError !== undefined && (
                                <FormHelperText error>
                                    {segmentTypeError}
                                </FormHelperText>
                            )}
                        </Stack>
                    ),
                },
            ]}
        />
    )
})
